import { useEffect, useMemo, useState } from 'react'
import './OrderDisputes.scss'
import filterIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Users/filter.svg'
import arrowLeft from '../../../../../../BoostXpressAdmin/Assets/Icon/Users/arrow-left.png'
import arrowRight from '../../../../../../BoostXpressAdmin/Assets/Icon/Users/arrow-right.svg'
import { HorizontalLoader } from 'Components'
import ModalWrapper from 'Components/ModalWrapper/ModalWrapper'
import { CurrencyFormatter, handleError, handleSuccess } from 'Helpers'
import { DateTimeFormatter } from 'Helpers/DateTimeFormatter'
import { ToastContainer } from 'react-toastify'
import { OrderDisputeXModel } from 'BoostXpressAdmin/Model/orderDisputeXModel'
import { orderDisputeBXService } from 'BoostXpressAdmin/Services/orderDisputeBXService'

const PAGE_SIZE = 25
const STATUS_SUGGESTIONS = ['PENDING', 'RESOLVED', 'CLOSED']

const OrderDisputes = () => {
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [sortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [isLoading, setIsLoading] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [total, setTotal] = useState(0)
  const [disputes, setDisputes] = useState<OrderDisputeXModel[]>([])

  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedDisputeId, setSelectedDisputeId] = useState('')
  const [selectedDispute, setSelectedDispute] = useState<OrderDisputeXModel | null>(null)
  const [isDetailsLoading, setIsDetailsLoading] = useState(false)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [statusDraft, setStatusDraft] = useState('')

  const loadDisputes = async () => {
    try {
      setIsLoading(true)
      const res = await orderDisputeBXService.list(pageNumber, PAGE_SIZE, sortOrder)
      const responseData = res.data?.data
      const pagination = res.data?.meta?.pagination ?? res.data?.data?.meta?.pagination
      const nextDisputes = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.dataset)
          ? responseData.dataset
          : []

      setDisputes(nextDisputes)
      setTotal(pagination?.total ?? nextDisputes.length)
      setPageSize(pagination?.per_page ?? PAGE_SIZE)
    } catch (error) {
      setDisputes([])
      setTotal(0)
      setPageSize(PAGE_SIZE)
      handleError({ response: { data: { error: 'Failed to fetch order disputes.' } } })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadDisputes()
  }, [pageNumber, sortOrder])

  const filteredDisputes = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()

    return disputes.filter((dispute) => {
      const matchesStatus = statusFilter ? dispute.status === statusFilter : true
      if (!matchesStatus) return false

      if (!query) return true

      const stationName =
        dispute.order?.station?.businessName || dispute.order?.station?.branchName || ''

      return [
        dispute.resourceId,
        dispute.reason,
        dispute.description,
        dispute.status,
        dispute.order?.referenceNumber,
        dispute.order?.product,
        stationName,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    })
  }, [disputes, searchTerm, statusFilter])

  const totalPages = useMemo(() => {
    if (!total) return 1
    return Math.max(1, Math.ceil(total / (pageSize || PAGE_SIZE)))
  }, [pageSize, total])

  const closeModal = () => {
    setShowDetailsModal(false)
    setSelectedDisputeId('')
    setSelectedDispute(null)
    setStatusDraft('')
    setIsDetailsLoading(false)
    setIsUpdatingStatus(false)
  }

  const openDisputeDetails = async (disputeId: string) => {
    try {
      setShowDetailsModal(true)
      setSelectedDisputeId(disputeId)
      setIsDetailsLoading(true)
      setSelectedDispute(null)
      const res = await orderDisputeBXService.show(disputeId)
      const dispute = res.data?.data ?? null
      setSelectedDispute(dispute)
      setStatusDraft(dispute?.status ?? '')
    } catch (error) {
      handleError({ response: { data: { error: 'Failed to fetch dispute details.' } } })
    } finally {
      setIsDetailsLoading(false)
    }
  }

  const handleUpdateStatus = async () => {
    const nextStatus = statusDraft.trim()
    if (!selectedDisputeId || !nextStatus) {
      handleError({ response: { data: { error: 'Status is required.' } } })
      return
    }

    try {
      setIsUpdatingStatus(true)
      const res = await orderDisputeBXService.updateStatus(selectedDisputeId, nextStatus)
      const updatedDispute =
        res.data?.data ?? (selectedDispute ? { ...selectedDispute, status: nextStatus } : null)

      if (updatedDispute) {
        setSelectedDispute(updatedDispute)
        setStatusDraft(updatedDispute.status)
        setDisputes((current) =>
          current.map((item) =>
            item.resourceId === updatedDispute.resourceId
              ? { ...item, status: updatedDispute.status }
              : item,
          ),
        )
      }

      handleSuccess('Dispute status updated successfully.')
    } catch (error) {
      handleError({ response: { data: { error: 'Failed to update dispute status.' } } })
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  return (
    <div className='bx-order-disputes'>
      <ToastContainer />

      <h2 className='bx-order-disputes__title'>Order Disputes</h2>

      <div className='bx-order-disputes__container'>
        <div className='bx-order-disputes__toolbar row g-2 align-items-center'>
          <div className='col-12 col-md-10 col-xxl-11'>
            <div className='bx-search-wrapper w-100'>
              <div className='bx-search-icon'>
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                    stroke='#868484'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <input
                type='text'
                placeholder='Search by reason or order reference'
                className='bx-search-input'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className='col-6 col-md-2 col-xxl-1 position-relative'>
            <button className='bx-action-btn w-100' onClick={() => setShowFilters((v) => !v)}>
              <img src={filterIcon} alt='' className='bx-action-icon' />
              <span className='bx-action-label'>Filters</span>
            </button>

            {showFilters ? (
              <div className='bx-order-disputes-filters-dropdown'>
                <div className='bx-filters-header'>
                  <h5>Filter Disputes</h5>
                  <button className='bx-filters-close' onClick={() => setShowFilters(false)}>
                    x
                  </button>
                </div>

                <div className='bx-filters-body'>
                  <div className='bx-filter-group'>
                    <label>Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Statuses</option>
                      {STATUS_SUGGESTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className='bx-filters-footer'>
                  <button
                    className='btn btn-secondary'
                    onClick={() => {
                      setStatusFilter('')
                      setShowFilters(false)
                    }}
                  >
                    Clear Filters
                  </button>
                  <button className='btn btn-primary' onClick={() => setShowFilters(false)}>
                    Apply Filters
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className='bx-table-wrapper'>
          <table className='bx-table'>
            <thead>
              <tr>
                <th className='bx-th-sn'>S/N</th>
                <th className='bx-th-order'>Order Ref</th>
                <th className='bx-th-reason'>Reason</th>
                <th className='bx-th-description'>Description</th>
                <th className='bx-th-status'>Status</th>
                <th className='bx-th-product'>Product</th>
                <th className='bx-th-station'>Station</th>
                <th className='bx-th-date'>Created</th>
                <th className='bx-th-action'>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className='bx-td-empty'>
                    <HorizontalLoader />
                  </td>
                </tr>
              ) : filteredDisputes.length === 0 ? (
                <tr>
                  <td colSpan={9} className='bx-td-empty'>
                    No order disputes found
                  </td>
                </tr>
              ) : (
                filteredDisputes.map((dispute, index) => {
                  const stationName =
                    dispute.order?.station?.businessName ||
                    dispute.order?.station?.branchName ||
                    '-'

                  return (
                    <tr key={dispute.resourceId}>
                      <td className='bx-td-sn'>{index + 1 + (pageNumber - 1) * pageSize}</td>
                      <td className='bx-td-order'>{dispute.order?.referenceNumber || '-'}</td>
                      <td className='bx-td-reason'>{dispute.reason || '-'}</td>
                      <td className='bx-td-description'>
                        <span className='bx-clamp-text'>{dispute.description || '-'}</span>
                      </td>
                      <td className='bx-td-status'>
                        <div className='bx-badge-wrapper'>
                          <span
                            className={`bx-badge bx-badge--${String(dispute.status || 'pending')
                              .toLowerCase()
                              .replace(/[^a-z0-9]+/g, '-')}`}
                          >
                            {dispute.status || '-'}
                          </span>
                        </div>
                      </td>
                      <td className='bx-td-product'>{dispute.order?.product || '-'}</td>
                      <td className='bx-td-station'>{stationName}</td>
                      <td className='bx-td-date'>{DateTimeFormatter(dispute.createdAt)}</td>
                      <td className='bx-td-action'>
                        <button
                          type='button'
                          className='bx-view-link bx-view-button'
                          onClick={() => openDisputeDetails(dispute.resourceId)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className='bx-pagination'>
          <button
            className='bx-pagination-btn'
            disabled={pageNumber <= 1 || isLoading}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          >
            <div className='bx-pagination-icon-wrap'>
              <img src={arrowLeft} alt='' className='bx-pagination-icon bx-pagination-icon--left' />
            </div>
            <span className='bx-pagination-text'>Previous</span>
          </button>

          <div className='bx-pagination-numbers'>
            <span className='bx-page-number bx-page-number--active'>{pageNumber}</span>
            <span className='bx-page-ellipsis'>/</span>
            <span className='bx-page-number'>{totalPages}</span>
          </div>

          <button
            className='bx-pagination-btn'
            disabled={pageNumber >= totalPages || isLoading}
            onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
          >
            <span className='bx-pagination-text'>Next</span>
            <img src={arrowRight} alt='' className='bx-pagination-icon' />
          </button>
        </div>
      </div>

      <ModalWrapper
        showModal={showDetailsModal}
        closeModal={closeModal}
        title='Order Dispute Details'
        size='lg'
      >
        <div className='bx-order-dispute-modal'>
          {isDetailsLoading ? (
            <HorizontalLoader />
          ) : !selectedDispute ? (
            <div className='bx-order-dispute-modal__empty'>Unable to load dispute details.</div>
          ) : (
            <>
              <div className='bx-order-dispute-modal__section'>
                <h4>Dispute Information</h4>
                <div className='bx-order-dispute-modal__grid'>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Dispute ID</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.resourceId}
                    </span>
                  </div>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Created At</span>
                    <span className='bx-order-dispute-modal__value'>
                      {DateTimeFormatter(selectedDispute.createdAt)}
                    </span>
                  </div>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Reason</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.reason || '-'}
                    </span>
                  </div>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Current Status</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.status || '-'}
                    </span>
                  </div>
                  <div className='bx-order-dispute-modal__item bx-order-dispute-modal__item--full'>
                    <span className='bx-order-dispute-modal__label'>Description</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.description || '-'}
                    </span>
                  </div>
                </div>
              </div>

              <div className='bx-order-dispute-modal__section'>
                <h4>Order Information</h4>
                <div className='bx-order-dispute-modal__grid'>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Order Reference</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.order?.referenceNumber || '-'}
                    </span>
                  </div>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Order Status</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.order?.status || '-'}
                    </span>
                  </div>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Product</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.order?.product || '-'}
                    </span>
                  </div>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Payment Status</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.order?.paymentStatus || '-'}
                    </span>
                  </div>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Quantity</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.order?.quantity ?? '-'}{' '}
                      {selectedDispute.order?.unitOfMeasure || ''}
                    </span>
                  </div>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Price</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.order?.currency || ''}
                      {typeof selectedDispute.order?.price === 'number'
                        ? CurrencyFormatter(selectedDispute.order.price)
                        : '-'}
                    </span>
                  </div>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Station</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.order?.station?.businessName ||
                        selectedDispute.order?.station?.branchName ||
                        '-'}
                    </span>
                  </div>
                  <div className='bx-order-dispute-modal__item'>
                    <span className='bx-order-dispute-modal__label'>Fuel Pump</span>
                    <span className='bx-order-dispute-modal__value'>
                      {selectedDispute.order?.fuelPump?.pumpNumber || '-'}
                    </span>
                  </div>
                </div>
              </div>

              <div className='bx-order-dispute-modal__section'>
                <h4>Update Status</h4>
                <div className='bx-order-dispute-modal__update-row'>
                  <select
                    className='bx-order-dispute-modal__input'
                    value={statusDraft}
                    onChange={(e) => setStatusDraft(e.target.value)}
                  >
                    <option value=''>Select status</option>
                    {STATUS_SUGGESTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                  <button
                    type='button'
                    className='bx-order-dispute-modal__submit'
                    disabled={isUpdatingStatus}
                    onClick={handleUpdateStatus}
                  >
                    {isUpdatingStatus ? 'Updating...' : 'Update Status'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </ModalWrapper>
    </div>
  )
}

export default OrderDisputes
